/**
 * Row and column totals for the anatomy grid.
 *
 * **A marginal is one of the header's grand totals restricted to a single row
 * or column** — the same measurement projected onto an axis, not a new one.
 * The header already prints `540 tensors · 4.63B params · 8.62 GiB · 16.00
 * bits/weight` for the tower; a family marginal is that, for `attn.q` alone.
 *
 * That framing is why this is one function and not three. `bytes` and `params`
 * project as plain sums; `bits/weight` is those two sums divided. So the
 * aggregation returns `{bytes, params}` per role and per layer, and the UI
 * reads whichever the active measure needs (SPEC §3).
 *
 * ## The rule everything else hangs off
 *
 * **The bpw marginal is `Σbytes·8 ÷ Σparams`, never the mean of the cells'
 * `effectiveBpw`** (SPEC §2.1). Averaging the cell numbers lets an 8 KB router
 * outvote 58 million weights and reports a layer as several times heavier than
 * it is — a wrong-but-authoritative number, which is the thing this project
 * refuses everywhere else.
 *
 * The two agree exactly when every cell in the group has equal params, which is
 * precisely the case this feature exists to break: a map worth summarizing is
 * one where allocation is *not* uniform. A "simplification" to the cell-mean
 * would therefore look correct on the easy models and be wrong on every
 * interesting one. `anatomy-marginals.test.ts` fails loudly on it.
 *
 * ## Exclusions are counted, never silent
 *
 * A cell whose scheme could not be resolved carries `params: 0`, so summing
 * drops it: correct arithmetic, silent omission. The counts travel with each
 * marginal so the UI can say "over 14 of 16 cells, 2 unresolved" (SPEC §2.3) —
 * the same honesty as the IQ coverage work, where a silent gap was the defect.
 *
 * Pure arithmetic over cells already in memory. **No requests, ever** (SPEC §7)
 * — this cannot touch the no-prefetch invariant because it has nothing to
 * fetch.
 */

import type { Anatomy, AnatomyCell } from "./anatomy.js";

/**
 * One row's or column's totals.
 *
 * `bytes` and `params` are the sums; `bpw` is the quotient, `undefined` when
 * nothing resolved. Kept together rather than collapsed to the active measure
 * so the UI can switch measures without recomputing, and so the reconciliation
 * gate can check all three against the header at once.
 */
export interface Marginal {
  readonly bytes: number;
  readonly params: number;
  /**
   * `Σbytes · 8 ÷ Σparams`, or `undefined` when no cell in the group resolved.
   *
   * `undefined` rather than `0`: a group with nothing to measure has no bits
   * per weight, and printing `0.00` would state a measurement of zero where the
   * truth is an absence. The same distinction the byte ledger draws with an em
   * dash for a dtype present on one side only.
   */
  readonly bpw: number | undefined;
  /** Cells in this group that contributed parameters. */
  readonly resolved: number;
  /** Cells in this group, including the ones that contributed nothing. */
  readonly total: number;
  /**
   * Cells holding non-parameter buffers — contributing no parameters, and not
   * for want of understanding them.
   *
   * Split out from the `total - resolved` difference because that difference
   * drives the trailing `*`, and the `*` means *"some cell could not be
   * resolved"*. A causal mask is resolved: we know what it is, we know it is
   * not a weight, and we say so in its own row. Letting it raise the marker
   * would report a coverage gap on `openai-community/gpt2`, whose every tensor
   * is understood — which is the precise failure the marker exists to prevent,
   * pointed the other way.
   */
  readonly buffers: number;
}

export interface AnatomyMarginals {
  /** Keyed by role, in whatever order the caller iterates `anatomy.roles`. */
  readonly byRole: ReadonlyMap<string, Marginal>;
  /** Keyed by layer index; the global column is keyed `undefined`. */
  readonly byLayer: ReadonlyMap<number | undefined, Marginal>;
  /**
   * The whole grid — what the corner prints, and what both axes must sum to.
   *
   * Computed from the same cells rather than read off `anatomy.budget`, and
   * that is deliberate: it makes the reconciliation gate a real check. Taking
   * the header's number here would make the corner agree with the header by
   * construction while the rows and columns quietly disagreed with both.
   */
  readonly total: Marginal;
}

/** Bytes held by cells that hold no parameters by nature. */
function bufferBytes(cells: readonly AnatomyCell[]): number {
  let n = 0;
  for (const cell of cells) if (cell.isBuffer) n += cell.bytes;
  return n;
}

/** `Σbytes·8 ÷ Σparams`, computed once over a group — never a mean of means. */
function marginalOf(cells: readonly AnatomyCell[]): Marginal {
  let bytes = 0;
  let params = 0;
  let resolved = 0;
  let buffers = 0;
  for (const cell of cells) {
    bytes += cell.bytes;
    params += cell.params;
    // `params > 0` rather than `!ambiguous`: what disqualifies a cell from the
    // average is contributing no weights to it. An ambiguous cell reports zero
    // params, so the two coincide today — but the arithmetic reason is the
    // durable one, and a cell that becomes resolvable later starts counting
    // without anyone remembering to update a flag test.
    if (cell.params > 0) resolved += 1;
    // Counted separately, never as an unresolved cell. See `buffers` above.
    if (cell.isBuffer) buffers += 1;
  }
  return {
    bytes,
    params,
    /*
     * Buffer bytes are in `bytes` and contribute no parameters, so a group
     * holding both weights and buffers would divide one by the other and report
     * a bits-per-weight that charges mask bytes to real weights — the same
     * error `anatomyTotals` refuses at the header level.
     *
     * It cannot arise today: buffers occupy their own role, so no group mixes
     * them with weights. Written as the subtraction anyway, because "cannot
     * arise" is a property of the current role table rather than of this
     * function, and the failure it would produce is a plausible number rather
     * than an obviously broken one.
     */
    bpw: params > 0 ? ((bytes - bufferBytes(cells)) * 8) / params : undefined,
    resolved,
    total: cells.length,
    buffers,
  };
}

/**
 * Project the grid onto both axes.
 *
 * Missing cells contribute nothing rather than zero, which is what makes a
 * sparse grid correct by construction: `attn.k` stopping early under KV-sharing
 * leaves those layers out of its family average instead of dragging it toward
 * zero. Treating an absent cell as `0 bpw` is the naive-mean trap wearing a
 * second costume (SPEC §4).
 */
export function computeMarginals(anatomy: Anatomy): AnatomyMarginals {
  const byRoleCells = new Map<string, AnatomyCell[]>();
  const byLayerCells = new Map<number | undefined, AnatomyCell[]>();
  const all: AnatomyCell[] = [];

  for (const cell of anatomy.cells.values()) {
    all.push(cell);
    const role = byRoleCells.get(cell.role) ?? [];
    role.push(cell);
    byRoleCells.set(cell.role, role);
    const layer = byLayerCells.get(cell.layer) ?? [];
    layer.push(cell);
    byLayerCells.set(cell.layer, layer);
  }

  return {
    byRole: new Map([...byRoleCells].map(([role, cells]) => [role, marginalOf(cells)])),
    byLayer: new Map(
      [...byLayerCells].map(([layer, cells]) => [layer, marginalOf(cells)]),
    ),
    total: marginalOf(all),
  };
}
